import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const EMPTY = { title: '', departmentId: '', positionId: '', description: '', opensAt: '', closesAt: '' };

export function JobPostingCreateModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const departments = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: () => hrApi.listDepartments().then((r) => r.items || []),
    enabled: open,
  });
  const positions = useQuery({
    queryKey: ['hr', 'positions', form.departmentId],
    queryFn: () =>
      hrApi.listPositions(form.departmentId ? { departmentId: form.departmentId } : undefined).then((r) => r.items || []),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (body) => hrApi.createJobPosting(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'job-postings'] });
      setForm(EMPTY);
      setError(null);
      if (onCreated) {
        onCreated(created);
      }
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      title: form.title.trim(),
      departmentId: form.departmentId || undefined,
      positionId: form.positionId || undefined,
      description: form.description.trim() || undefined,
      opensAt: form.opensAt || undefined,
      closesAt: form.closesAt || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('rec.postings.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('rec.postings.modal.titleField')}</span>
          <input className="input" value={form.title} onChange={handle('title')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('rec.postings.modal.department')}</span>
            <select className="select" value={form.departmentId} onChange={handle('departmentId')}>
              <option value="">{t('hr.employees.modal.optional')}</option>
              {(departments.data || []).map((d) => (
                <option key={d.id} value={d.id}>{d.code} - {d.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('rec.postings.modal.position')}</span>
            <select className="select" value={form.positionId} onChange={handle('positionId')}>
              <option value="">{t('hr.employees.modal.optional')}</option>
              {(positions.data || []).map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('rec.postings.modal.opensAt')}</span>
            <input className="input" type="date" value={form.opensAt} onChange={handle('opensAt')} />
          </label>
          <label className="field">
            <span className="field-label">{t('rec.postings.modal.closesAt')}</span>
            <input className="input" type="date" value={form.closesAt} onChange={handle('closesAt')} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('rec.postings.modal.description')}</span>
          <textarea className="textarea" rows={3} value={form.description} onChange={handle('description')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
